/**
 * Processed order-card border PNG cache (white-key → transparent via canvas).
 * BorderImageWithTransparency dedupes network + pixel work per unique src.
 */

import { resolvePublicAssetUrl } from './resolvePublicAssetUrl';

/** Every faction / protect / mix border asset used by getBorderImagePath in OrderDesign.js */
export const ALL_ORDER_BORDER_PATHS = Object.freeze([
  '/images/border_shadow.png',
  '/images/border_humans.png',
  '/images/border_exolot.png',
  '/images/border_ra.png',
  '/images/border_asrah.png',
  '/images/border_sentients.png',
  '/images/border_mix.png',
  '/images/border_protect_multifaction.png',
  '/images/border_protect_remnants.png',
  '/images/border_protect_league.png',
  '/images/border_protect_ra.png',
  '/images/border_protect_exolot.png',
  '/images/border_protect_sentients.png',
  '/images/border_protect_asrah.png',
]);

/** @typedef {{ aspectRatio: number, imageData: ImageData, naturalWidth: number, naturalHeight: number }} ProcessedBorder */

const processedBySrc = new Map();
const inflightBySrc = new Map();

function makeWhitePixelsTransparent(imageData) {
  const data = imageData.data;
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const a = data[i + 3];
    const isWhite =
      (r > 230 && g > 230 && b > 230) ||
      (r > 240 && g > 240 && b > 240) ||
      r + g + b > 700;
    if (isWhite && a > 0) {
      data[i + 3] = 0;
    }
  }
  return imageData;
}

/**
 * @param {string} src - Root-relative border path, e.g. /images/border_humans.png
 * @returns {Promise<ProcessedBorder>}
 */
export function loadBorderImage(src) {
  if (!src || typeof src !== 'string') {
    throw new Error('loadBorderImage: src must be a non-empty string');
  }

  const cached = processedBySrc.get(src);
  if (cached) {
    return Promise.resolve(cached);
  }

  const inflight = inflightBySrc.get(src);
  if (inflight) {
    return inflight;
  }

  const promise = new Promise((resolve, reject) => {
    if (typeof document === 'undefined') {
      reject(new Error('loadBorderImage: document is not available'));
      return;
    }

    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d', { alpha: true, willReadFrequently: false });
      if (!ctx) {
        inflightBySrc.delete(src);
        reject(new Error(`loadBorderImage: 2d context unavailable for ${src}`));
        return;
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      const imageData = makeWhitePixelsTransparent(ctx.getImageData(0, 0, canvas.width, canvas.height));

      /** @type {ProcessedBorder} */
      const processed = {
        aspectRatio: img.height / img.width,
        imageData,
        naturalWidth: img.naturalWidth,
        naturalHeight: img.naturalHeight,
      };

      processedBySrc.set(src, processed);
      inflightBySrc.delete(src);
      resolve(processed);
    };

    img.onerror = () => {
      inflightBySrc.delete(src);
      reject(new Error(`loadBorderImage failed: ${src}`));
    };

    img.src = resolvePublicAssetUrl(src);
  });

  inflightBySrc.set(src, promise);
  return promise;
}

/**
 * @param {string} src
 * @returns {ProcessedBorder | null}
 */
export function getCachedBorderImage(src) {
  return processedBySrc.get(src) || null;
}

/**
 * Warm every order border asset (menus / strategy grid).
 * @param {string[]} [paths]
 * @returns {Promise<PromiseSettledResult<ProcessedBorder>[]>}
 */
export function preloadAllOrderBorderImages(paths = ALL_ORDER_BORDER_PATHS) {
  if (!Array.isArray(paths) || paths.length === 0) {
    throw new Error('preloadAllOrderBorderImages: paths must be a non-empty array');
  }
  return Promise.allSettled(paths.map((path) => loadBorderImage(path)));
}
